import { FC } from "react";
import Message, { MessageRating } from "../../../models/conversation/Message";
import MessageBubbleMarkdown from "./MessageBubbleMarkdown";
import MessageBubbleActionBar from "./MessageBubbleActionBar";
import BouncingDot from "./BouncingDot";
import { Avatar, AvatarFallback, AvatarImage, Button } from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import { useContent } from "@/hooks/useContent";
import { User, RefreshCw, AlertCircle } from "lucide-react";
import {
  getRetryGenerationLabels,
  isGenerationFailedMessage,
} from "@/lib/chatGenerationErrors";
import { useConversationHub } from "@/hooks/useConversationHub";

type Props = {
  message: Message;
  bubbleTestId?: string;
  isOverview?: boolean;
};

const MessageBubble: FC<Props> = ({
  message,
  bubbleTestId,
  isOverview = false,
}) => {
  const { t } = useContent();
  const { retryFailedResponse, waitingForResponse } = useConversationHub();
  const isFailedResponse = isGenerationFailedMessage(message);
  const retryLabels = getRetryGenerationLabels(message.language);

  const handleRetry = () => {
    if (waitingForResponse || isOverview) {
      return;
    }
    void retryFailedResponse(message);
  };

  return (
    <div className="flex flex-col">
      <div
        className={cn(
          "rounded-xl p-4 w-fit text-sm relative max-w-full",
          isOverview &&
            message.rating !== MessageRating.NeutralVote &&
            !message.human &&
            "mb-7",
          message.human
            ? "bg-primary dark:bg-primary/75 text-primary-foreground flex self-end"
            : isFailedResponse
              ? "bg-destructive/10 border border-destructive/30 self-start"
              : "bg-assistant-message"
        )}
        data-testid={bubbleTestId}
      >
        {/* Avatar - only show for regular assistant messages */}
        {!message.human && (
          <div className="absolute top-0 -left-10">
            <Avatar className="size-8">
              <AvatarImage src={t("chat.icon")} />
            </Avatar>
          </div>
        )}
        {message.human && isOverview && (
          <div className="absolute top-0 -right-10">
            <Avatar className="size-8">
              <AvatarFallback>
                <User className="text-primary" />
              </AvatarFallback>
            </Avatar>
          </div>
        )}

        {!message.human && message.isClosed && !isFailedResponse && (
          <MessageBubbleActionBar message={message} isOverview={isOverview} />
        )}

        {/* Message content */}
        {!message.human && message.text === "" && !message.isClosed ? (
          <div className="flex gap-2">
            <BouncingDot msDelay={0} />
            <BouncingDot msDelay={100} />
            <BouncingDot msDelay={200} />
          </div>
        ) : isFailedResponse ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-2 text-destructive">
              <AlertCircle className="size-4 mt-0.5 shrink-0" aria-hidden />
              <MessageBubbleMarkdown
                message={message}
                isOverview={isOverview}
                exposeCopySource
              />
            </div>
            {!isOverview && (
              <Button
                id={`retry-failed-response-${message.id}`}
                type="button"
                variant="outline"
                size="sm"
                className="w-fit border-destructive/40 text-destructive hover:bg-destructive/10"
                onClick={handleRetry}
                disabled={waitingForResponse}
                aria-label={retryLabels.ariaLabel}
              >
                <RefreshCw className="size-4 mr-2" aria-hidden />
                {retryLabels.button}
              </Button>
            )}
          </div>
        ) : (
          <MessageBubbleMarkdown
            message={message}
            isOverview={isOverview}
            exposeCopySource
          />
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
